import { Component, inject, input, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { KENDO_LABEL } from '@progress/kendo-angular-label';
import { AbstractNewComponent } from '@unity/abstract/abstract-new.component';
import { AgencyMiniDto, RolesClient, UserRoleCreatedResponseDto, UserRoleCreateDto, UserRolesClient, UsersClient } from '@unity/clients';
import { PageContainerComponent } from '@unity/components/layouts/page-container/page-container.component';
import { DropdownAgencyControlComponent } from '@unity/forms/dropdown-agency-control/dropdown-agency-control.component';
import { DropdownControlComponent } from '@unity/forms/dropdown-control/dropdown-control.component';
import { TextboxReadonlyControlComponent } from '@unity/forms/textbox-readonly-control/textbox-readonly-control.component';
import { CardNewSharedComponent } from '@unity/shared/composed/card-new-shared/card-new-shared.component';

import { nonZeroFormControl } from '@unity/utils/form-controls';
import { FormGroupControlSchema, UnityRouteUrlTrees } from '@unity/utils/types';
import { EMPTY } from 'rxjs';

class NewRoleMembershipSchema implements FormGroupControlSchema<UserRoleCreateDto> {
  public user_id = nonZeroFormControl();
  public agency_id = nonZeroFormControl();
  public role_id = nonZeroFormControl();
  public constructor(user_id?: number) {
    this.user_id = nonZeroFormControl({ value: user_id });
  }
}

@Component({
  selector: 'unity-new-user-role-page',
  standalone: true,
  imports: [
    KENDO_LABEL,
    ReactiveFormsModule,
    DropdownAgencyControlComponent,
    DropdownControlComponent,
    TextboxReadonlyControlComponent,
    PageContainerComponent,
    CardNewSharedComponent,
  ],
  templateUrl: './new-user-role-page.component.html',
  styleUrl: './new-user-role-page.component.scss',
})
export class NewUserRolePageComponent extends AbstractNewComponent<UserRoleCreateDto, NewRoleMembershipSchema, UserRoleCreatedResponseDto> {
  private readonly rolesClient = inject(RolesClient);
  private readonly usersClient = inject(UsersClient);
  private readonly userRolesClient = inject(UserRolesClient);

  public readonly $userId = input.required({ alias: 'userId', transform: (value: string) => parseInt(value) });

  protected override readonly $contentHeader = signal('Assign User to a Business Partner');
  protected override createResourceFn$ = (form: FormGroup<NewRoleMembershipSchema>) => {
    const userId = this.$userId();
    if (userId) {
      return this.userRolesClient.createOneForUser(userId, form.getRawValue());
    } else {
      return EMPTY;
    }
  };
  protected override redirectUrlFn = (response: UserRoleCreatedResponseDto): UnityRouteUrlTrees => ['/user', response.user_id];
  protected override newControlsFn(): NewRoleMembershipSchema {
    const userId = this.$userId();
    return new NewRoleMembershipSchema(userId);
  }

  private readonly $selectedAgency = signal<AgencyMiniDto | undefined>(undefined);

  protected userResource = rxResource({
    params: () => {
      const userId = this.$userId();
      return userId ? { userId } : undefined;
    },
    stream: ({ params: { userId } }) => this.usersClient.getOneAsMini(userId),
  });

  protected validRolesResource = rxResource({
    defaultValue: [],
    params: () => {
      const userId = this.$userId();
      const agencyId = this.$selectedAgency()?.id;

      return userId && agencyId ? { agencyId, userId } : undefined;
    },
    stream: ({ params: { agencyId, userId } }) => this.rolesClient.getAllAvailableForAgencyUserAsMini(agencyId, userId),
  });

  protected $inputRoles = this.validRolesResource.value;

  protected agencyHandler(event: AgencyMiniDto) {
    this.$selectedAgency.set(event);
  }
}
